import type { InputBaseComponentProps } from "@mui/material";
import { InputAdornment, InputBase, Typography, useTheme } from "@mui/material";
import { forwardRef } from "react";

export interface NumberInputProps
  extends Omit<InputBaseComponentProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  maxValue?: number;
  id?: string;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChange, suffix, maxValue, id, sx, ...props }, ref) => {
    const { colors, customSpacing, radius, typography } = useTheme();

    const handleNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      let numericOnly = e.target.value.replace(/\D/g, "");

      if (maxValue && numericOnly && Number(numericOnly) > maxValue) {
        return;
      }

      if (/^0+$/.test(numericOnly)) {
        numericOnly = "0";
      } else {
        numericOnly = numericOnly.replace(/^0+/, "");
      }
      onChange(numericOnly);
    };

    return (
      <InputBase
        ref={ref}
        type="text"
        value={value ?? ""}
        onChange={handleNumberInput}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
          min: 1,
        }}
        endAdornment={
          suffix ? (
            <InputAdornment position="end">
              <Typography
                variant="body3"
                sx={{
                  color: colors.text.secondary,
                  background: "transparent",
                  px: customSpacing.xs,
                  cursor: "default",
                  userSelect: "none",
                }}
              >
                {suffix}
              </Typography>
            </InputAdornment>
          ) : undefined
        }
        id={id}
        sx={{
          width: "100%",
          minHeight: 40,
          fontFamily: typography.fontFamily,
          fontSize: typography.body1.fontSize,
          borderRadius: radius.md,
          gap: customSpacing.none,
          padding: `${customSpacing.xs} ${customSpacing.md}`,
          border: `1px solid ${colors.border.primary}`,
          background: colors.background.primary,
          color: colors.text.primary,
          boxSizing: "border-box",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:focus": {
            outline: "none",
          },
          ...sx,
        }}
        {...props}
      />
    );
  },
);
